// Single source of truth for enum values and badge colors shared across pages.
// Shapes needed by individual pages (option lists, {key,label} pairs) are all
// derived from these maps so they can't drift apart.

export const CATEGORY_LABELS = {
  VUE: 'Vue Monitor',
  CHARGER: 'EV Charger',
  SMART_PLUG: 'Smart Plug',
  HOME_BATTERY: 'Home Battery',
  THERMOSTAT: 'Thermostat',
  UNKNOWN: 'Unknown',
}

// Models available under each category. THERMOSTAT has no dedicated models.
export const CATEGORY_MODELS = {
  VUE: ['V1', 'V2', 'V3', 'VUC'],
  CHARGER: ['C1', 'C2'],
  SMART_PLUG: ['P1'],
  HOME_BATTERY: ['SOLAX', 'ALPHA'],
  THERMOSTAT: [],
  UNKNOWN: [],
}

export const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS)

// Bundle creation targets real hardware categories only.
export const BUNDLE_CATEGORIES = CATEGORY_OPTIONS
  .filter(key => key !== 'UNKNOWN')
  .map(key => ({ key, label: CATEGORY_LABELS[key] }))

export const MODEL_OPTIONS = [
  ...new Set([...Object.values(CATEGORY_MODELS).flat(), 'UNKNOWN_MODEL']),
]

// Models offered on the add-device form for a given category. UNKNOWN_MODEL is
// always allowed (thermostats and oddballs have no dedicated model).
export function modelsForCategory(category) {
  const models = CATEGORY_MODELS[category] ?? []
  if (!category) return MODEL_OPTIONS
  return models.includes('UNKNOWN_MODEL') ? models : [...models, 'UNKNOWN_MODEL']
}

export const ENVIRONMENTS = ['DEV3', 'PROD']

export const LOCATION_OPTIONS = [
  'UNKNOWN',
  'LITTLETON_GARAGE',
  'LITTLETON_SERVER_ROOM',
  'LITTLETON_QA_BENCH',
  'LITTLETON_QA_CHARGER_CART',
  'OUTSIDE_OFFICE',
]

// Short display labels: every location is at the Littleton site, so the prefix
// only costs table width. The stored/API value stays the enum name.
export const LOCATION_LABELS = {
  UNKNOWN: 'Unknown',
  LITTLETON_GARAGE: 'Garage',
  LITTLETON_SERVER_ROOM: 'Server room',
  LITTLETON_QA_BENCH: 'QA bench',
  LITTLETON_QA_CHARGER_CART: 'QA charger cart',
  OUTSIDE_OFFICE: 'Outside office',
}

export function locationLabel(location) {
  return LOCATION_LABELS[location] ?? location
}

export const CATEGORY_COLORS = {
  CHARGER: 'badge-secondary',
  SMART_PLUG: 'badge-accent',
  VUE: 'badge-primary',
  HOME_BATTERY: 'badge-info',
  THERMOSTAT: 'badge-success',
  UNKNOWN: 'badge-neutral',
}

export const ENV_COLORS = {
  DEV3: 'badge-warning',
  PROD: 'badge-error',
}

// Federations are a customer-pool concept; partner accounts have none.
export const FEDERATIONS = [
  { key: 'emporia', label: 'Emporia' },
  { key: 'google', label: 'Google' },
  { key: 'apple', label: 'Apple' },
]

// Which Emporia directory an account lives in. CUSTOMER is an ordinary app user;
// PARTNER is a partner-portal user under a partner organization, with its own
// login pool and its own device-association path.
export const ACCOUNT_TYPE_LABELS = {
  CUSTOMER: 'Customer',
  PARTNER: 'Partner',
}

export const ACCOUNT_TYPES = Object.keys(ACCOUNT_TYPE_LABELS)

export const ACCOUNT_TYPE_COLORS = {
  CUSTOMER: 'badge-ghost',
  PARTNER: 'badge-info',
}

// Mirrors the cloud's PartnerUserRole. Exactly one ROOT per partner org.
export const PARTNER_ROLES = ['ROOT', 'ADMIN', 'CONTRIBUTOR', 'VIEWER']
